/**
 * TabStrip.tsx — a plain two-item tab switcher rendered as `<a href="#…">`
 * links (hash navigation, no router — matches `App.tsx`'s route model).
 * Active-tab styling mirrors `Sidebar.tsx`'s `isActive` color pattern
 * (`text-gold`) plus a bottom border, since a horizontal tab strip signals
 * "current" with an underline rather than a filled background.
 *
 * Keyboard: roving tabindex (only the active tab is in the Tab order) and
 * arrow-key navigation via `onTabListKeyDown`, shared with the agentic config
 * editor's tab rail (WAI-ARIA tabs pattern, WCAG 2.1.1).
 */

import type { KeyboardEvent } from 'react';

/**
 * Arrow/Home/End navigation for a `role=tablist`; attach as `onKeyDown` on each tab: focuses the neighbouring
 * tab and activates it (click), so the panel follows focus. All four arrows
 * are accepted because whether the list is a row or a column is CSS-only.
 */
export const onTabListKeyDown = (event: KeyboardEvent<HTMLElement>) => {
  const tabs = Array.from(
    event.currentTarget
      .closest('[role="tablist"]')
      ?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [],
  );
  const current = tabs.indexOf(event.currentTarget);
  if (current < 0) return;
  const last = tabs.length - 1;
  const targets: Record<string, number> = {
    ArrowRight: current === last ? 0 : current + 1,
    ArrowDown: current === last ? 0 : current + 1,
    ArrowLeft: current === 0 ? last : current - 1,
    ArrowUp: current === 0 ? last : current - 1,
    Home: 0,
    End: last,
  };
  const next = tabs[targets[event.key] ?? -1];
  if (next == null) return;
  event.preventDefault();
  next.focus();
  next.click();
};

export interface TabStripItem {
  /** Stable id, used as the test selector (`tab-<id>`). */
  id: string;
  label: string;
  /** Full hash href, e.g. `#numbers` or `#numbers/products`. */
  href: string;
}

export interface TabStripProps {
  items: readonly TabStripItem[];
  activeId: string;
  /** Id of the panel the tabs control (`aria-controls`); omit when no such element exists. */
  panelId?: string;
}

export const TabStrip = ({ items, activeId, panelId = undefined }: TabStripProps) => (
  <div role="tablist" className="flex gap-4 border-b border-outline">
    {items.map((item) => {
      const isActive = item.id === activeId;
      return (
        <a
          key={item.id}
          href={item.href}
          role="tab"
          aria-selected={isActive}
          aria-controls={panelId}
          onKeyDown={onTabListKeyDown}
          tabIndex={isActive ? 0 : -1}
          data-testid={`tab-${item.id}`}
          className={`-mb-px border-b-2 px-1 py-2 text-body ${
            isActive ? 'border-gold text-gold' : 'border-transparent text-ink-3 hover:text-ink-1'
          }`}
        >
          {item.label}
        </a>
      );
    })}
  </div>
);
